/**
 * Persist bulk-booking idempotency keys across reload/tab recovery.
 * Stores only {clientScope, sourceHash, idempotencyKey} — never raw CSV.
 */

export type BulkIdempotencyRecord = {
  clientScope: string;
  sourceHash: string;
  idempotencyKey: string;
};

export const BULK_IDEMPOTENCY_STORAGE_KEY = 'dispatchdash.bulkBooking.idempotency.v1';

export function clientScopeKey(opts: { isPortal: boolean; clientId: string }): string {
  return opts.isPortal ? 'portal:self' : `client:${opts.clientId || ''}`;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function readBulkIdempotency(
  storage: StorageLike | null | undefined,
): BulkIdempotencyRecord | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BULK_IDEMPOTENCY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BulkIdempotencyRecord>;
    if (
      typeof parsed.clientScope !== 'string' ||
      typeof parsed.sourceHash !== 'string' ||
      typeof parsed.idempotencyKey !== 'string'
    ) {
      return null;
    }
    return {
      clientScope: parsed.clientScope,
      sourceHash: parsed.sourceHash,
      idempotencyKey: parsed.idempotencyKey,
    };
  } catch {
    return null;
  }
}

export function writeBulkIdempotency(
  storage: StorageLike | null | undefined,
  record: BulkIdempotencyRecord,
): void {
  if (!storage) return;
  storage.setItem(BULK_IDEMPOTENCY_STORAGE_KEY, JSON.stringify(record));
}

export function clearBulkIdempotency(storage: StorageLike | null | undefined): void {
  if (!storage) return;
  storage.removeItem(BULK_IDEMPOTENCY_STORAGE_KEY);
}

/**
 * Resolve the key to use for a validated payload.
 * Reuses the stored key when clientScope + sourceHash match; otherwise rotates.
 */
export function resolveBulkIdempotencyKey(opts: {
  storage: StorageLike | null | undefined;
  clientScope: string;
  sourceHash: string;
  newKey: () => string;
}): string {
  const existing = readBulkIdempotency(opts.storage);
  if (
    existing &&
    existing.clientScope === opts.clientScope &&
    existing.sourceHash === opts.sourceHash &&
    existing.idempotencyKey
  ) {
    return existing.idempotencyKey;
  }
  const key = opts.newKey();
  writeBulkIdempotency(opts.storage, {
    clientScope: opts.clientScope,
    sourceHash: opts.sourceHash,
    idempotencyKey: key,
  });
  return key;
}
